import { Box, Button, Card, Paper, Stack, Typography } from '@mui/material';
import { arrayMove } from '@dnd-kit/sortable';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider, useGetList, useLocale, useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { Iconify } from 'src/components/iconify';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';
import { Resources } from '../../resource';
import CalendarGrid from './components/CalendarGrid';
import DayEventsDialog from './components/DayEventsDialog';
import dayjs from 'dayjs';
import { CALENDAR_END, CALENDAR_START } from './utils';

const formatHintDate = (day: string) => {
  const parsed = dayjs(day);
  return parsed.isValid() ? parsed.format('DD/MM') : day;
};

const formatTimezone = () => {
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  return tzName ? `${tzName} (UTC${sign}${hh}:${mm})` : `UTC${sign}${hh}:${mm}`;
};
import './init';

export const MainPage = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const locale = useLocale();
  const notify = useNotify();
  const [events, setEvents] = useState<any[]>([]);
  const [initialEvents, setInitialEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  const { data: allEvents = [], isLoading: allEventsLoading } = useGetList(
    Resources.worldCupBreakingEventsEvents,
    {
      meta: { acceptLanguage: locale },
    }
  );

  const isChanged = useMemo(() => {
    if (events.length !== initialEvents.length) return true;
    return events.some((m, i) => {
      const o = initialEvents[i];
      return !o || `${m.id}` !== `${o.id}` || (m.day ?? '') !== (o.day ?? '');
    });
  }, [events, initialEvents]);

  const countByDay = useMemo(() => {
    const counts: Record<string, number> = {};
    events.forEach((m) => {
      const day = m.day ?? '';
      if (!day) return;
      counts[day] = (counts[day] ?? 0) + 1;
    });
    return counts;
  }, [events]);

  const dayByMarketId = useMemo(() => {
    const result: Record<string, string> = {};
    events.forEach((m) => {
      if (m.day) result[`${m.id}`] = m.day;
    });
    return result;
  }, [events]);

  const dayItems = useMemo(() => {
    if (!selectedDay) return [];
    return events.filter((m) => m.day === selectedDay);
  }, [events, selectedDay]);

  useEffect(() => {
    let active = true;

    const loadEvents = async () => {
      setLoading(true);
      try {
        const response = await dataProvider.getList(Resources.worldCupBreakingEvents, {
          filter: {},
          pagination: { page: 1, perPage: 200 },
          sort: { field: 'id', order: 'ASC' },
          meta: { acceptLanguage: locale },
        });
        const items: any[] = response?.data ?? [];
        const localItems = items.map((item) => {
          const parsed = item.startTime ? dayjs(item.startTime) : null;
          if (parsed && parsed.isValid()) {
            return { ...item, day: parsed.format('YYYY-MM-DD') };
          }
          return item;
        });
        if (!active) return;
        setEvents(localItems);
        setInitialEvents(localItems);
      } catch (error: any) {
        if (!active) return;
        notify(error?.message || translate('module.worldCup.breakingEvents.loadError'), {
          type: 'error',
        });
      } finally {
        if (active) setLoading(false);
      }
    };

    loadEvents();

    return () => {
      active = false;
    };
  }, [dataProvider, locale, notify, translate]);

  const handleSelectDay = (day: string) => setSelectedDay(day);
  const handleCloseDayDialog = () => setSelectedDay(null);

  const handleReorderInDay = (oldIdx: number, newIdx: number) => {
    if (!selectedDay) return;
    const reordered = arrayMove(dayItems, oldIdx, newIdx);
    setEvents((current) => {
      let dayPtr = 0;
      return current.map((m) => {
        if (m.day !== selectedDay) return m;
        const next = reordered[dayPtr];
        dayPtr += 1;
        return next;
      });
    });
  };

  const handleRemoveInDay = (idx: number) => {
    const target = dayItems[idx];
    if (!target) return;
    setEvents((current) =>
      current.filter((m) => !(m.day === selectedDay && `${m.id}` === `${target.id}`))
    );
  };

  const handleSelectItem = (item: any) => {
    if (!selectedDay) return;
    if (events.some((m) => `${m.id}` === `${item.id}`)) {
      notify(translate('module.worldCup.breakingEvents.alreadyExists'), { type: 'warning' });
      return;
    }
    setEvents((current) => [...current, { ...item, day: selectedDay }]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dataProvider.create(Resources.worldCupBreakingEvents, {
        data: {
          items: events.map((e) => ({
            type: (e.type as string) || 'EVENT',
            id: Number(e.id),
            day: e.day ?? CALENDAR_START,
          })),
        },
      });
      setInitialEvents(events);
      notify(translate('module.worldCup.breakingEvents.saveSuccess'), { type: 'success' });
    } catch (error: any) {
      notify(error?.message || translate('module.worldCup.breakingEvents.saveError'), {
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = useCallback(() => {
    if (!window.confirm(translate('module.worldCup.breakingEvents.confirmDiscard'))) return;
    setEvents(initialEvents);
  }, [initialEvents, translate]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={translate('module.worldCup.breakingEvents.title')}
        links={[
          { name: translate('resource.worldCup') },
          { name: translate('module.worldCup.breakingEvents.title') },
        ]}
        sx={{ mb: 3 }}
        action={
          <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
            <Button
              variant="contained"
              size="medium"
              color="secondary"
              startIcon={<Iconify icon="ph:note" />}
              onClick={() => setAuditLogOpen(true)}
            >
              {translate('resource.auditLogs')}
            </Button>
          </PermissionGuard>
        }
      />

      <Card sx={{ p: 3 }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            {translate('module.worldCup.breakingEvents.loading')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {translate('module.worldCup.breakingEvents.calendarHint', {
                start: formatHintDate(CALENDAR_START),
                end: formatHintDate(CALENDAR_END),
                timezone: formatTimezone(),
              })}
            </Typography>
            <CalendarGrid countByDay={countByDay} onSelectDay={handleSelectDay} />
          </Stack>
        )}
      </Card>

      <DayEventsDialog
        open={Boolean(selectedDay)}
        day={selectedDay ?? ''}
        items={dayItems}
        dayByMarketId={dayByMarketId}
        events={allEvents}
        eventsLoading={allEventsLoading}
        onClose={handleCloseDayDialog}
        onReorder={handleReorderInDay}
        onRemove={handleRemoveInDay}
        onSelectItem={handleSelectItem}
      />

      <AuditLogDialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        resource="world-cup-custom-breaking"
        title={`${translate('resource.auditLogs')} - ${translate('module.worldCup.breakingEvents.title')}`}
      />

      {isChanged ? (
        <SaveBar loading={saving} onDiscard={handleDiscard} onSave={handleSave} />
      ) : null}
    </DashboardContent>
  );
};

const SaveBar = memo(
  ({
    loading,
    onDiscard,
    onSave,
  }: {
    loading: boolean;
    onDiscard: () => void;
    onSave: () => void;
  }) => {
    const translate = useTranslate();

    return (
      <Paper
        elevation={8}
        sx={{
          position: 'sticky',
          bottom: 16,
          mt: 3,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {translate('module.worldCup.breakingEvents.saveBar.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('module.worldCup.breakingEvents.saveBar.desc')}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" disabled={loading} onClick={onDiscard}>
            {translate('actions.discard')}
          </Button>
          <PermissionButton resource="world-cup-custom-breaking" action="write">
            <Button variant="contained" disabled={loading} onClick={onSave}>
              {loading ? translate('common.saving') : translate('actions.save')}
            </Button>
          </PermissionButton>
        </Stack>
      </Paper>
    );
  }
);

SaveBar.displayName = 'SaveBar';

export default MainPage;
